import type { SelectedWorkbookExport } from "@/core/components/workbook-export";
import { PayrollRunStatus } from "@/payroll/payroll.d";
import type { PayStatement } from "./statement/statement-schema";

export interface IAiScriptGeneratedWorkbookMetadata {
  filename?: string;
  content_type?: string;
  sheet_count?: number;
  cell_count?: number;
}

export interface IAiScriptRun {
  id: string;
  status: string;
  created_at: string;
  generated_workbook_available: boolean;
  generated_workbook_url: string | null;
  generated_workbook_metadata: IAiScriptGeneratedWorkbookMetadata | null;
  /** Tabs this run rewrites; null means the whole workbook. */
  sheet_scope?: string[] | null;
  /** Runs ahead of this one in the period's queue; null once it starts. */
  queue_position?: number | null;
}

export interface ICreateAiScriptRunResponse {
  run: IAiScriptRun;
  /** True when the request matched a run already queued, so nothing was created. */
  coalesced?: boolean;
}

/**
 * One entry in a pay period's run queue: the run executing plus everything
 * waiting behind it, oldest first. Runs execute one at a time per period.
 */
export interface IActiveAiScriptRun {
  id: string;
  status: string;
  /**
   * Tabs this run rewrites. `null` = the whole workbook. An EMPTY array means the
   * run is scoped, but every tab in it is hidden from this user — the API redacts
   * the names rather than confirm which departments exist.
   */
  sheet_scope: string[] | null;
  /**
   * Whether this run changes anything this user can see. False for a rerun of a
   * department they have no access to, which is the case where telling them a run is
   * in progress would be both noise and a lie — their results will not update.
   * Absent on an older API; treated as true so a deploy skew over-notifies rather
   * than silently hiding a run that does matter.
   */
  affects_visible_sheets?: boolean;
}

export interface IAiSpreadsheetCellAlignment {
  horizontal?: "left" | "center" | "right" | string | null;
  vertical?: "top" | "center" | "bottom" | string | null;
  wrap_text?: boolean | null;
}

export interface IAiSpreadsheetCellFormat {
  bold?: boolean | null;
  background_color?: string | null;
  bg_color?: string | null;
  fill_color?: string | null;
  font_color?: string | null;
  fg_color?: string | null;
  alignment?: IAiSpreadsheetCellAlignment | null;
  format_code?: string | null;
}

export interface IAiSpreadsheetCellError {
  code?: string | null;
  message?: string | null;
}

// `source_ref` is a free-form jsonb blob the generator attaches to pay-amount
// cells for traceability. Known keys below; `technician_external_id` is what
// lets a multi-technician (team) sheet attribute each row to a person.
export interface IAiSpreadsheetCellSourceRef {
  job_id?: number | string | null;
  invoice_id?: number | string | null;
  sku?: string | null;
  commission_category?: string | null;
  technician_external_id?: string | number | null;
  [key: string]: unknown;
}

export const AI_SPREADSHEET_CELL_KIND = {
  formula: "formula",
  value: "value",
} as const;

export type AiSpreadsheetCellKind =
  (typeof AI_SPREADSHEET_CELL_KIND)[keyof typeof AI_SPREADSHEET_CELL_KIND];

// Outcome the backend records for a persistent edit each run: applied silently,
// applied-but-flagged (a source input moved / the key was ambiguous), or dropped
// because the edit's row no longer exists this run.
export const AI_SPREADSHEET_OVERRIDE_OUTCOME = {
  autoAccepted: "auto_accepted",
  needsReview: "needs_review",
  unmapped: "unmapped",
} as const;

export type AiSpreadsheetOverrideOutcome =
  (typeof AI_SPREADSHEET_OVERRIDE_OUTCOME)[keyof typeof AI_SPREADSHEET_OVERRIDE_OUTCOME];

// Why a needs_review outcome was flagged, so the panel shows the right copy
// instead of inferring it. Null unless the outcome is needs_review.
export const AI_SPREADSHEET_OVERRIDE_REVIEW_REASON = {
  drifted: "drifted",
  ambiguous: "ambiguous",
} as const;

export type AiSpreadsheetOverrideReviewReason =
  (typeof AI_SPREADSHEET_OVERRIDE_REVIEW_REASON)[keyof typeof AI_SPREADSHEET_OVERRIDE_REVIEW_REASON];

// The cell formats the backend accepts (AiSpreadsheetCell::VALUE_TYPES). Used by
// the "Format cell" menu; `boolean` exists but isn't offered as a menu option.
export const AI_SPREADSHEET_VALUE_TYPE = {
  number: "number",
  currency: "currency",
  percent: "percent",
  text: "text",
  date: "date",
  boolean: "boolean",
} as const;

export type AiSpreadsheetValueType =
  (typeof AI_SPREADSHEET_VALUE_TYPE)[keyof typeof AI_SPREADSHEET_VALUE_TYPE];

// Why a region override needs review. Regions reconcile by exact header text, so
// the failure modes differ from a cell override's: `ambiguous` means the saved
// header values matched in more than one place this run, `conflict` means the
// match collided with a region the script already generated.
export const AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON = {
  ambiguous: "ambiguous",
  conflict: "conflict",
} as const;

export type AiSpreadsheetRegionOverrideReviewReason =
  (typeof AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON)[keyof typeof AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON];

// One place the saved header values matched on this run. Confirming a review
// sends back the chosen header_row/start_col.
export interface IAiSpreadsheetRegionOverrideCandidate {
  header_row: number;
  start_col: number;
  end_col: number;
}

/**
 * A durable region configuration that could not be re-applied cleanly after a
 * rerun. The API parks these instead of guessing; until one is confirmed the
 * user's saved region is missing from the sheet.
 */
export interface IAiSpreadsheetRegionOverrideReview {
  id: string;
  sheet_name: string;
  recipient_external_id: string | null;
  outcome: AiSpreadsheetOverrideOutcome;
  review_reason: AiSpreadsheetRegionOverrideReviewReason | null;
  header_values: string[];
  configuration: IAiSpreadsheetRegionInput;
  candidates: IAiSpreadsheetRegionOverrideCandidate[];
  replaces_generated_region: boolean;
  deletes_generated_region: boolean;
  edited_by: { id: string; full_name: string | null } | null;
}

export interface IAiSpreadsheetRegionOverrideConfirmResponse {
  // Null when confirming applies a deletion tombstone rather than a region.
  region: IAiSpreadsheetRegion | null;
  override: { id: string; outcome: AiSpreadsheetOverrideOutcome };
}

export interface IAiSpreadsheetMergedRange {
  start_row: number;
  end_row: number;
  start_col: number;
  end_col: number;
}

export interface IAiSpreadsheetRegionColumnSetting {
  filterable: boolean;
  sortable: boolean;
  declared_type: AiSpreadsheetValueType | null;
}

export interface IAiSpreadsheetRegionValidation {
  valid: boolean;
  errors: string[];
}

export interface IAiSpreadsheetRegionInput {
  header_row: number;
  record_start_row: number;
  record_end_row: number;
  start_col: number;
  end_col: number;
  column_settings: Record<string, IAiSpreadsheetRegionColumnSetting>;
}

export interface IAiSpreadsheetRegion extends IAiSpreadsheetRegionInput {
  id: string;
  ai_spreadsheet_sheet_id: string;
  origin: "script" | "user_confirmed";
  created_at: string;
  updated_at: string;
  validation: IAiSpreadsheetRegionValidation;
}

export interface IAiSpreadsheetRegionResponse {
  region: IAiSpreadsheetRegion;
}

const AI_SPREADSHEET_NUMERIC_VALUE_TYPES = [
  AI_SPREADSHEET_VALUE_TYPE.number,
  AI_SPREADSHEET_VALUE_TYPE.currency,
  AI_SPREADSHEET_VALUE_TYPE.percent,
] as const;

export type AiSpreadsheetNumericValueType =
  (typeof AI_SPREADSHEET_NUMERIC_VALUE_TYPES)[number];

export const isAiSpreadsheetNumericValueType = (
  value: string | null | undefined,
): value is AiSpreadsheetNumericValueType =>
  value != null &&
  AI_SPREADSHEET_NUMERIC_VALUE_TYPES.includes(
    value as AiSpreadsheetNumericValueType,
  );

// Excel number-format code per value type — the frontend source of truth,
// mirroring AiSpreadsheetCell::DEFAULT_FORMAT_CODES on the API. Types without an
// entry (number/text/boolean) render as their raw value, so formatting to one
// clears any prior format_code.
export const AI_SPREADSHEET_DEFAULT_FORMAT_CODE: Partial<
  Record<AiSpreadsheetValueType, string>
> = {
  [AI_SPREADSHEET_VALUE_TYPE.currency]: "$#,##0.00",
  [AI_SPREADSHEET_VALUE_TYPE.percent]: "0.00%",
  [AI_SPREADSHEET_VALUE_TYPE.date]: "yyyy-mm-dd",
};

export interface IAiSpreadsheetCellOverrideValue {
  raw_input?: string | null;
  kind?: string | null;
  value_type?: string | null;
  computed_value?: string | number | boolean | null;
}

// A durable manual edit that survives re-runs, re-applied by content key. Mirrors
// the ICM IcmCellOverride shape; `previous_value` is the original generated
// baseline and `generated_value` is what the script produced on the latest run.
// Where an edit sits in the workbook this run (for the review UI's cell chip
// and "go to cell"). Absent for dropped edits (no live cell).
export interface IAiSpreadsheetCellLocation {
  sheet_id: string;
  sheet_name: string | null;
  row: number;
  col: number;
  address: string;
}

export interface IAiSpreadsheetCellOverride {
  id: string;
  override_key: string;
  outcome: AiSpreadsheetOverrideOutcome | null;
  review_reason: AiSpreadsheetOverrideReviewReason | null;
  stale: boolean;
  stale_at: string | null;
  value: IAiSpreadsheetCellOverrideValue | null;
  previous_value: string | number | boolean | null;
  generated_value: string | number | boolean | null;
  edited_by: { id: string; full_name: string | null } | null;
  edited_at: string;
  superseded_at: string | null;
  current: boolean;
  cell?: IAiSpreadsheetCellLocation | null;
}

// Emitted when the user selects (single-click) a cell that carries a persistent
// edit, so a parent surface can open the focused per-cell review view.
export interface IAiScriptEditedCellSelection {
  override: IAiSpreadsheetCellOverride;
  coordinate: string;
}

export interface IAiSpreadsheetCell {
  id: string;
  ai_spreadsheet_sheet_id: string;
  row: number;
  col: number;
  kind: string | null;
  raw_input: string | null;
  computed_value: string | number | boolean | null;
  value_type: string | null;
  format: IAiSpreadsheetCellFormat | null;
  error: string | IAiSpreadsheetCellError | null;
  accessor_key: string | null;
  source_ref: IAiSpreadsheetCellSourceRef | null;
  suggested_values: string[] | null;
  // A script-declared read-only cell (e.g. the label column of a manual add-on
  // row). Not editable in the grid.
  locked?: boolean | null;
  edited_by_admin_user_id: string | null;
  edited_at: string | null;
  // Present only when a durable manual edit is currently applied at this cell.
  override?: IAiSpreadsheetCellOverride | null;
  // Set on cells belonging to a user-added (manual) row — the row's stable id.
  manual_row_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface IAiSpreadsheetSheet {
  id: string;
  ai_spreadsheet_id: string;
  name: string;
  recipient_external_id: string | null;
  access_key: string | null;
  editable: boolean;
  loaded: boolean;
  cell_count: number;
  empty: boolean;
  /**
   * Whether the current user may rerun this tab on its own. False for a tab that
   * totals up other tabs — it has to regenerate whenever its sources do — and false
   * unless this user personally holds approval access for it, since a rerun replaces
   * numbers a department may already have signed off. The create endpoint and the
   * queued-run re-check apply the same rule, so a control never appears for a
   * request the API would refuse.
   */
  rerun_scopable: boolean;
  position: number | null;
  column_widths: Record<string, number> | number[] | null;
  row_heights: Record<string, number> | number[] | null;
  frozen_column: number | null;
  // Older API payloads can omit this during a deploy. Treat that as no regions.
  regions?: IAiSpreadsheetRegion[];
  // Rectangular merged-cell ranges, one-based and inclusive. A region may not
  // intersect any of them, so the region editor checks these before saving.
  merged_ranges?: IAiSpreadsheetMergedRange[];
  // Saved regions for this sheet that failed to re-apply after a rerun. Excludes
  // auto-accepted overrides, so a non-empty list always needs user attention.
  region_override_reviews?: IAiSpreadsheetRegionOverrideReview[];
  created_at: string;
  updated_at: string;
  cells?: IAiSpreadsheetCell[];
}

export interface IAiSpreadsheetLoadedSheet extends IAiSpreadsheetSheet {
  loaded: true;
  cells: IAiSpreadsheetCell[];
}

export interface IAiSpreadsheet {
  id: string;
  ai_script_run_id?: string;
  profitability_report_run_id?: string;
  business_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  sheets: IAiSpreadsheetSheet[];
  // Workbook-wide roll-up of the same reviews. Carries overrides whose old sheet
  // identity no longer exists this run, which no per-sheet list can surface.
  region_override_reviews?: IAiSpreadsheetRegionOverrideReview[];
}

export interface IAiSpreadsheetResponse {
  spreadsheet: IAiSpreadsheet;
}

export interface IAiSpreadsheetSheetResponse {
  sheet: IAiSpreadsheetLoadedSheet;
}

export const AI_SPREADSHEET_OPERATION_STATUS = {
  queued: "queued",
  running: "running",
  succeeded: "succeeded",
  failed: "failed",
} as const;

export type AiSpreadsheetOperationStatus =
  (typeof AI_SPREADSHEET_OPERATION_STATUS)[keyof typeof AI_SPREADSHEET_OPERATION_STATUS];

export interface AiSpreadsheetOperationResult {
  version?: number;
  changed_cell_ids?: string[];
  changed_sheet_ids?: string[];
  manual_row_ids?: string[];
  deleted_cell_ids?: string[];
  override_id?: string;
}

export interface AiSpreadsheetOperation {
  id: string;
  operation_type: string;
  status: AiSpreadsheetOperationStatus;
  spreadsheet_id: string;
  ai_script_run_id: string;
  idempotency_key: string;
  result: AiSpreadsheetOperationResult | null;
  error: { type: string; message: string } | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AcceptedAiSpreadsheetOperationResponse {
  operation: AiSpreadsheetOperation;
  idempotent_replay: boolean;
}

export interface IAiSpreadsheetPasteEdit {
  cell_id: string;
  raw_input: string;
  kind?: AiSpreadsheetCellKind;
  value_type?: AiSpreadsheetValueType;
}

export interface IAiScriptCellOverridesResponse {
  overrides: IAiSpreadsheetCellOverride[];
}

export interface IAcceptAiScriptCellOverrideResponse {
  override: IAiSpreadsheetCellOverride;
}

// A row an ambiguous edit could belong to (for the reviewer's picker): its
// location plus the value it currently shows, to tell the duplicate rows apart.
export interface IAiSpreadsheetOverrideCandidate
  extends IAiSpreadsheetCellLocation {
  cell_id: string;
  value: string | number | boolean | null;
}

export interface IAiScriptOverrideCandidatesResponse {
  candidates: IAiSpreadsheetOverrideCandidate[];
}

export interface IUpdateAiSpreadsheetSheetResponse {
  sheet: Pick<
    IAiSpreadsheetSheet,
    "id" | "column_widths" | "row_heights" | "frozen_column"
  >;
}

// Placement outcomes a run records for a user-added row. Only a run sets these,
// so a null outcome means the row has never been through one.
export const AI_SPREADSHEET_MANUAL_ROW_OUTCOME = {
  // The row's anchor resolved; it went back where the user put it.
  placed: "placed",
  // The anchor matched no row (or several), so the row was appended to the end
  // of the sheet instead of its intended position.
  needsReview: "needs_review",
} as const;

export type AiSpreadsheetManualRowOutcome =
  (typeof AI_SPREADSHEET_MANUAL_ROW_OUTCOME)[keyof typeof AI_SPREADSHEET_MANUAL_ROW_OUTCOME];

export interface IAiSpreadsheetManualRow {
  id: string;
  sheet_name: string;
  recipient_external_id: string | null;
  cells: Record<string, IManualRowCellInput>;
  anchor: Record<string, unknown>;
  ordinal: number;
  outcome: AiSpreadsheetManualRowOutcome | null;
  // Who added the row and when. `added_by` is null for rows added before the API
  // recorded it separately from the last editor, and on older API payloads during
  // a deploy — the panel then dates the row without naming anyone.
  added_by?: { id: string; full_name: string | null } | null;
  added_at?: string | null;
  // Who last edited one of the row's cells, and when. Both null until someone
  // edits the row after adding it — a manual-row cell edit rewrites the row's
  // seed rather than creating an override, so this is its only audit trail.
  edited_by?: { id: string; full_name: string | null } | null;
  edited_at?: string | null;
  // The row's live anchor-cell location (for the panel's "go to row" link).
  // Absent when the row has no live cells this run.
  cell?: IAiSpreadsheetCellLocation | null;
}

// The durable seed for one column of a manual row. Formula intent is explicit
// so a literal string beginning with `=` can remain an ordinary value.
export interface IManualRowValueCellInput {
  kind?: typeof AI_SPREADSHEET_CELL_KIND.value;
  value: string | number | boolean | null;
  value_type?: string | null;
}

export interface IManualRowFormulaCellInput {
  kind: typeof AI_SPREADSHEET_CELL_KIND.formula;
  raw_input: string;
  value_type?: string | null;
}

export type IManualRowCellInput =
  | IManualRowValueCellInput
  | IManualRowFormulaCellInput;

export interface IManualRowInput {
  cells: Record<string, IManualRowCellInput>;
  anchor?: { after_row?: number };
}

export interface IManualRowsResponse {
  manual_rows: IAiSpreadsheetManualRow[];
}

export type AiScriptExplanationChatRole = "user" | "assistant";

export interface IAiScriptExplanationChatMessage {
  role: AiScriptExplanationChatRole;
  content: string;
}

export interface IAiScriptExplanationChatSource {
  id?: string | null;
  type?: string | null;
  title?: string | null;
  label?: string | null;
  detail?: string | null;
  description?: string | null;
  job_id?: string | null;
  invoice_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface IAiScriptExplanationChatRunContext {
  business_id: string;
  ai_script_run_id: string;
  ai_script_version_id: string | null;
  pay_period_id: string | null;
  status: string;
  pay_period?: {
    start_date: string | null;
    end_date: string | null;
  } | null;
  generated_workbook?: {
    filename?: string | null;
    sheet_count?: number | null;
    cell_count?: number | null;
    content_type?: string | null;
  } | null;
}

export const AI_SCRIPT_CHAT_CONTEXT_KIND = {
  cell: "cell",
  row: "row",
  column: "column",
} as const;

export type AiScriptChatContextKind =
  (typeof AI_SCRIPT_CHAT_CONTEXT_KIND)[keyof typeof AI_SCRIPT_CHAT_CONTEXT_KIND];

export interface IAiScriptChatCellContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.cell;
  sheetName: string;
  coordinate: string;
  value: string | null;
  columnHeader: string | null;
  rowLabel: string | null;
}

export interface IAiScriptChatRowContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.row;
  sheetName: string;
  rowNumber: number;
  rowLabel: string | null;
  cells: { header: string | null; value: string }[];
}

export interface IAiScriptChatColumnContext {
  kind: typeof AI_SCRIPT_CHAT_CONTEXT_KIND.column;
  sheetName: string;
  columnLetter: string;
  columnHeader: string | null;
  values: { rowLabel: string | null; value: string }[];
  isTruncated: boolean;
}

export type AiScriptChatSelectionContext =
  | IAiScriptChatCellContext
  | IAiScriptChatRowContext
  | IAiScriptChatColumnContext;

export interface IAiScriptExplanationChatError {
  code: string;
  message: string;
}

export interface IAiScriptExplanationChatResponse {
  message: string;
  sources: IAiScriptExplanationChatSource[];
  run_context: IAiScriptExplanationChatRunContext | null;
  error: IAiScriptExplanationChatError | null;
}

export interface IAiScriptExplanationChatThread {
  id: string;
  name: string;
  purpose: string;
  status: string;
  subject_type: string;
  subject_id: string;
  created_by_id: string | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  running: boolean;
  active_turn: IAiScriptExplanationChatActiveTurn | null;
}

export interface IAiScriptExplanationChatActiveTurn {
  turn_id: string;
  status: string;
  started_at: string | null;
  updated_at: string | null;
}

export const AI_CHAT_MESSAGE_ROLE = {
  user: "user",
  assistant: "assistant",
  error: "error",
} as const;

export type AiChatMessageRole =
  (typeof AI_CHAT_MESSAGE_ROLE)[keyof typeof AI_CHAT_MESSAGE_ROLE];

export interface IAiScriptExplanationChatPersistedMessage {
  id: string;
  role: AiChatMessageRole;
  content: string;
  sequence: number;
  metadata: {
    sources?: IAiScriptExplanationChatSource[] | null;
    run_context?: IAiScriptExplanationChatRunContext | null;
    error?: IAiScriptExplanationChatError | null;
  } | null;
  created_by_id: string | null;
  created_at: string;
}

export interface IAiScriptExplanationChatTurnResponse {
  turn_id: string;
  thread: IAiScriptExplanationChatThread;
}

export interface IAiScriptExplanationChatThreadsResponse {
  threads: IAiScriptExplanationChatThread[];
}

export interface IAiScriptExplanationChatThreadResponse {
  thread: IAiScriptExplanationChatThread;
  messages: IAiScriptExplanationChatPersistedMessage[];
}

export interface IAiScriptExplanationChatStreamEvent {
  kind:
    | "event_started"
    | "progress"
    | "text_delta"
    | "event_finished"
    | "error";
  content?: string | null;
  seq: number;
}

export interface IAiScriptExplanationChatStreamResponse {
  status: string;
  events: IAiScriptExplanationChatStreamEvent[];
  cursor: number;
}

export type AiScriptRunStatus = "pending" | "running" | "completed" | "failed";

export type AiScriptRunDisplayStatus = PayrollRunStatus | null;

/**
 * Why a technician's pay for a period is being withheld.
 *
 * Businesses finish payroll differently, so the honest message differs:
 * `awaiting_send` is waiting on a PayPack, `period_open` on the pay period itself
 * to end. Told to expect a send, a technician at a business that never emails
 * PayPacks waits on nothing and goes chasing an email nobody sent.
 */
export const STATEMENT_RELEASE_REASON = {
  awaitingSend: "awaiting_send",
  periodOpen: "period_open",
} as const;

export type StatementReleaseReason =
  (typeof STATEMENT_RELEASE_REASON)[keyof typeof STATEMENT_RELEASE_REASON];

/**
 * The technician statement endpoint's response.
 *
 * Every field but the statement itself is optional for deploy skew: an api that
 * predates the release gate sends none of them, and a missing `released` must read
 * as released rather than withholding every statement at every business.
 */
export interface IAiScriptStatementResponse {
  statement: PayStatement | null;
  linked: boolean;
  /**
   * Whether the pay for this period is theirs to see. False means `statement` is
   * null for that reason — distinct from having no pay at all, which is the same
   * payload with `released: true`.
   */
  released?: boolean;
  /** Which wait it is, when `released` is false. */
  release_reason?: StatementReleaseReason;
  /**
   * The run these figures came from, which for a technician is the run their pay was
   * SENT from — not necessarily the run that was asked for. Pack questions have to
   * attach to this one: filed against the requested run, a question about $1,190
   * arrives pointing at a rerun that says $1,240, and whoever answers it cannot find
   * the line.
   */
  statement_run_id?: string;
}

export type ExplanationChatPathBuilder = (
  businessId: string,
  runId: string,
) => string;

export type WorkbookExportOptions = Partial<SelectedWorkbookExport>;

export type WorkbookExportResult =
  | { status: "processing"; token?: string }
  | { status: "ready"; file: Blob; filename?: string };
